export class DateService {
  /**
   * Возвращает границы недели (понедельник — воскресенье) для заданного смещения от текущей.
   * Неделя считается по ISO: понедельник — первый день, воскресенье — последний.
   *
   * @param weekIndex - Смещение в неделях: 0 — текущая неделя, 1 — предыдущая, 2 — две недели назад и т.д.
   * @returns Объект с полями start (Date понедельника) и end (Date воскресенья) выбранной недели.
   */
  public static getWeekRange(weekIndex: number): { start: Date; end: Date } {
    const d = new Date();
    d.setDate(d.getDate() - weekIndex * 7);

    const daysToMonday = (d.getDay() + 6) % 7;
    const monday = new Date(d);

    monday.setDate(d.getDate() - daysToMonday);

    const sunday = new Date(monday);

    sunday.setDate(monday.getDate() + 6);

    return { start: monday, end: sunday };
  }

  /**
   * Форматирует диапазон дат выбранной недели в строку вида «9 февр - 15 февр».
   * Использует короткое название месяца без точки; локаль задаётся параметром.
   *
   * @param weekIndex - Смещение в неделях: 0 — текущая неделя, 1 — предыдущая и т.д.
   * @param locale - Локаль для Intl.DateTimeFormat (например, 'ru', 'en-US').
   * @returns Строка с диапазоном дат недели.
   */
  public static formatWeekRange(weekIndex: number, locale: string): string {
    const { start, end } = DateService.getWeekRange(weekIndex);
    const formatter = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" });
    const range = `${formatter.format(start)} - ${formatter.format(end)}`;

    return range.replace(/\./g, "");
  }

  /**
   * Форматирует диапазон «последние 7 дней»: от (сегодня −7) до (сегодня -1).
   * Строка вида «2 февр - 8 февр».
   *
   * @param locale - Локаль для Intl.DateTimeFormat (например, 'ru', 'en-US').
   * @returns Строка с диапазоном дат.
   */
  public static formatLast7DaysRange(locale: string): string {
    const end = new Date();
    const start = new Date();

    start.setDate(start.getDate() - 7);
    end.setDate(end.getDate() - 1);

    const formatter = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" });
    const range = `${formatter.format(start)} - ${formatter.format(end)}`;

    return range.replace(/\./g, "");
  }

  /**
   * Возвращает дату, отстоящую от сегодня на заданное число дней назад, отформатированную как «день месяц»
   * (например, «9 февраля») в указанной локали.
   *
   * @param daysAgo - Сколько дней назад: 0 — сегодня, 1 — вчера, 2 — позавчера и т.д.
   * @param locale - Локаль для Intl.DateTimeFormat (например, 'ru', 'en-US').
   * @returns Строка с отформатированной датой.
   */
  public static getDateDaysAgo(daysAgo: number, locale: string): string {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);

    return new Intl.DateTimeFormat(locale, { day: "numeric", month: "long" }).format(date);
  }

  /**
   * Возвращает название дня недели для даты, отстоящей от сегодня на заданное число дней назад,
   * в указанной локали (например, «понедельник», «вторник»).
   *
   * @param daysAgo - Сколько дней назад: 0 — сегодня, 1 — вчера и т.д.
   * @param locale - Локаль для Intl.DateTimeFormat (например, 'ru', 'en-US').
   * @returns Название дня недели.
   */
  public static getWeekdayDaysAgo(daysAgo: number, locale: string): string {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);

    return new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date);
  }

  /**
   * Возвращает название месяца для заданного смещения от текущего.
   *
   * @param monthOffset - Смещение в месяцах: 0 — текущий месяц, 1 — предыдущий, 2 — два месяца назад и т.д.
   * @param locale - Локаль для Intl.DateTimeFormat (например, 'ru', 'en-US').
   * @returns Название месяца в длинном формате (например, «Февраль», «Январь»).
   */
  public static getMonthNameByOffset(monthOffset: number, locale: string): string {
    const date = new Date();
    date.setMonth(date.getMonth() - monthOffset);

    return new Intl.DateTimeFormat(locale, { month: "long" }).format(date);
  }

  /**
   * Форматирует дату в виде «день краткий_месяц, ЧЧ:ММ» (например, «8 янв, 16:55»).
   * Короткое название месяца без точки; время в 24-часовом формате.
   *
   * @param date_string - дата в виде строки (ISO 8601 или формат, поддерживаемый Date)
   * @param locale - локаль для Intl.DateTimeFormat (например, 'ru', 'en-US')
   * @returns строка вида «8 янв, 16:55»
   */
  public static formatDateWithTime(date_string: string, locale: string): string {
    const date = new Date(date_string);

    const date_part = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" })
      .format(date)
      .replace(/\./g, "");
    const time_part = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", hour12: false }).format(
      date,
    );

    return `${date_part}, ${time_part}`;
  }
}
